import Avatar, { AvatarSize } from 'components/avatar/Avatar'
import CustomFormInput from 'components/input/custom-form-input/CustomFormInput'
import { useTranslation } from 'react-i18next'
import { FORM, PLACEHOLDERS } from 'locales/locales'
import styled from 'styled-components'
import { FixMeLater, IDropdownOption } from 'types'
import { useEffect, useState } from 'react'
import CustomText from 'components/text/CustomText'
import DropdownListWithSearch from 'components/kendo/dropdown/DropdownListWithSearch'
import colors from 'utils/colors'
import { Chip } from '@progress/kendo-react-buttons'
import { isFlagSetIn } from 'utils/helpers/isSetInFlags'
import { AdminPermissions, IRolePermissions } from 'types/role.types'
import { useAppSelector } from 'app/hooks'

interface IUserInfoProps {
  id: string
  avatar: string
  fullname: string
  email: string
  title: string
  isDeleted: boolean
  formChecked: boolean
  selectedOption?: FixMeLater
  onChange: (value: string | boolean, key: string) => void
  dropDownData?: { teams?: Array<FixMeLater>; roles?: Array<FixMeLater> }
  setDropDownData: React.Dispatch<
    React.SetStateAction<{ teams?: Array<FixMeLater>; roles?: Array<FixMeLater> }>
  >
  selectedOptions: {
    roleId: Array<{ title: string; value: string }>
    teamId: Array<{ title: string; value: string }>
  }
  onChangeInput: (value: string, key: string) => void
  // NewsfeedTeamManagement.tsx'den alınan dropdown search props'ları
  setDropdownDataState: (filter: FixMeLater) => void
  handleDropdownSearch: (
    skip: number,
    filter: string,
  ) => Promise<{ data: FixMeLater[]; total: number }>
}

const UserInfo = ({
  id,
  avatar,
  fullname,
  title,
  email,
  isDeleted,
  // selectedOption,
  onChange,
  dropDownData,
  selectedOptions,
  onChangeInput,
  setDropdownDataState,
  handleDropdownSearch,
  setDropDownData,
}: IUserInfoProps) => {
  const { t } = useTranslation()

  const [selectedTeam, setSelectedTeam] = useState<FixMeLater[]>([])

  useEffect(() => {
    onChangeInput(selectedTeam as FixMeLater, 'teamId')
  }, [selectedTeam, onChangeInput])

  useEffect(() => {
    if (selectedOptions?.teamId?.length) {
      setSelectedTeam(selectedOptions?.teamId as FixMeLater)
    }
    }, [selectedOptions?.teamId])

  const permissions = useAppSelector<IRolePermissions>((state) => state.auth.permissions)
  const hasUserUpdatePermission = isFlagSetIn([AdminPermissions.UPDATE_USER], permissions.admin)
  const hasUserRolesShowPermission = isFlagSetIn([AdminPermissions.VIEW_USER_ROLES], permissions.admin)
  const hasTeamsShowPermission = isFlagSetIn([AdminPermissions.VIEW_USER_TEAMS], permissions.admin)
  
  return (
    <InfoModalWrapper>
      <AvatarWrapper>
        <Avatar source={avatar} text={fullname} size={AvatarSize.extraLarge} />
      </AvatarWrapper>
      <FormWrapper>
        <CustomFormInput
          disabled
          value={fullname}
          title={t(FORM.FULLNAME)}
          placeholder={t(PLACEHOLDERS.FULLNAME)}
          onChange={(value: string) => onChange(value, 'fullname')}
        />
        <CustomFormInput
          disabled
          value={email}
          title={t(FORM.EMAIL)}
          placeholder={t(PLACEHOLDERS.EMAIL)}
          onChange={(value: string) => onChange(value, 'email')}
        />
        <CustomFormInput
          disabled={!hasUserUpdatePermission || isDeleted}
          value={title}
          title={t(FORM.TITLE)}
          placeholder={t(PLACEHOLDERS.TITLE)}
          onChange={(value: string) => onChange(value, 'title')}
        />
        {hasTeamsShowPermission && (
        <FormFieldWrapper>
          <CustomText typography="header3">{t(FORM.TEAM)}</CustomText>
          <StyledDropDownListWithSearch
            selectedOption={
              selectedTeam?.length > 0
                ? selectedTeam[selectedTeam.length - 1]
                : ({ title: t(FORM.TEAM_SELECT), value: null } as IDropdownOption)
            }
            options={
              dropDownData?.teams?.filter(
                (team: FixMeLater) => team && !selectedTeam?.some((t: FixMeLater) => t.value === team.value),
              ) || []
            }
            onChangeOption={(option: IDropdownOption) => {
              setSelectedTeam((prevState: FixMeLater) => [...prevState, option] as FixMeLater)
            }}
            setFilterDataState={setDropdownDataState}
            setDropdownData={(data: FixMeLater) => {
              setDropDownData((prevState: FixMeLater) => ({ ...prevState, teams: data }))
            }}
            filterDataState={undefined}
            /* defaultItem={{ title: t(PLACEHOLDERS.TEAM_PLACEHOLDER) }} */
            requestData={handleDropdownSearch}
            style={{ width: '100%' }}
          />
        </FormFieldWrapper>
        )}
        {hasTeamsShowPermission && selectedTeam?.length > 0 && (
          <MultipleSearchWrapper>
            {selectedTeam?.map((team: FixMeLater) => {
              return (
                <ChipWrapper
                  key={team.value}
                  text={team.title}
                  value={team.value}
                  removable={true}
                  size={'small'}
                  themeColor={'info'}
                  fillMode={'solid'}
                  disabled={false}
                  onRemove={() =>
                    setSelectedTeam(selectedTeam.filter((t: FixMeLater) => t.value !== team.value))
                  }
                />
              )
            })}
          </MultipleSearchWrapper>
        )}
        {hasUserRolesShowPermission && (
        <CustomFormInput
          type={'text'}
          dropdownType="MultiSelect"
          title={t(FORM.USER_ROLE)}
          dropDownData={dropDownData?.roles || []}
          selectedOption={selectedOptions.roleId}
          onChange={(value: string) => onChangeInput(value, 'roleId')}
        />
        )}
      </FormWrapper>
    </InfoModalWrapper>
  )
}

export default UserInfo

const InfoModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
`
const AvatarWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`
const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
`

const CollapsibleTagsWrapper = styled.div`
  width: 100%;
  height: 5rem;
`

const FormFieldWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`

const StyledDropDownListWithSearch = styled(DropdownListWithSearch)`
  width: 100%;
  border-radius: 4px;
  height: 25px;
  margin-top: 0px;

  .k-picker {
    border: 1px solid #ddd;
    height: 30px;
  }

  .k-input-inner {
    padding: 0.5rem;
    font-size: 14px;
  }

  &:focus {
    outline: 2px solid #90caf9;
    outline-offset: 2px;
  }
`

const MultipleSearchWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  column-gap: 6px;
  row-gap: 3px;
  margin-left: 5px;
  margin-bottom: 20px;
`
const ChipWrapper = styled(Chip)`
  background-color: ${colors.vime20};
  .k-chip-label,
  .k-chip-text {
    padding: 1px 3px;
  }
`
